import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, imgUrl, money, normalizeTiers } from "@/lib/api";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { Plus, Pencil, Trash2, Loader2, Upload, Sparkles, FileUp, Globe, X, Wand2, ImageDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { title: "", price: "", category: "General", description: "", image_url: "", in_stock: true, stock: 100, variant_name: "", variant_options: [], price_tiers: [], label: "", custom_text: "" };
const DEFAULT_TIERS = [5, 10, 25, 50, 100, 250, 500, 1000].map((q) => ({ min_qty: q, price: "" }));

export default function ProductsPage() {
  const { stores, selectedStore, setSelectedStore } = useStore();
  const storeId = selectedStore && selectedStore !== "all" ? selectedStore : (stores[0]?.id || "");
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [aiBusy, setAiBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [aliOpen, setAliOpen] = useState(false);
  const [aliConfigured, setAliConfigured] = useState(false);
  const [aliQ, setAliQ] = useState("");
  const [aliResults, setAliResults] = useState([]);
  const [aliLoading, setAliLoading] = useState(false);
  const [aliSelected, setAliSelected] = useState([]);
  const [aliImporting, setAliImporting] = useState(false);
  const [amzOpen, setAmzOpen] = useState(false);
  const [amzConfigured, setAmzConfigured] = useState(false);
  const [amzQ, setAmzQ] = useState("");
  const [amzResults, setAmzResults] = useState([]);
  const [amzLoading, setAmzLoading] = useState(false);
  const [amzSelected, setAmzSelected] = useState([]);
  const [amzImporting, setAmzImporting] = useState(false);
  const [impStores, setImpStores] = useState([]);
  const [impMode, setImpMode] = useState("csv");
  const [csvText, setCsvText] = useState("");
  const [impForm, setImpForm] = useState(empty);
  const [impBusy, setImpBusy] = useState(false);
  const csvFileRef = useRef();
  const [searchParams, setSearchParams] = useSearchParams();
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [scrapeBusy, setScrapeBusy] = useState(false);
  const [scraped, setScraped] = useState([]);
  const [scrapePlatform, setScrapePlatform] = useState("");
  const [autoEnrich, setAutoEnrich] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [enrichProgress, setEnrichProgress] = useState(0);
  const [optimizing, setOptimizing] = useState(false);

  const load = (id) => id && api.get(`/stores/${id}/products`).then((r) => setProducts(r.data));

  const openAli = async () => {
    setAliOpen(true); setAliResults([]); setAliSelected([]); setAliQ("");
    try { const r = await api.get("/admin/aliexpress/status"); setAliConfigured(!!r.data.configured); }
    catch { setAliConfigured(false); }
  };
  const aliSearch = async () => {
    if (!aliQ.trim()) return;
    setAliLoading(true);
    try { const r = await api.get(`/admin/aliexpress/search?q=${encodeURIComponent(aliQ.trim())}`); setAliResults(r.data.products || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Search failed"); }
    finally { setAliLoading(false); }
  };
  const toggleAli = (p) => setAliSelected((s) => s.some((x) => x.external_id === p.external_id) ? s.filter((x) => x.external_id !== p.external_id) : [...s, p]);
  const aliImport = async () => {
    if (!storeId) { toast.error("Select a store first"); return; }
    setAliImporting(true);
    try {
      const products = aliSelected.map((p) => ({ title: p.title, price: p.price, image_url: p.image_url, affiliate_url: p.affiliate_url }));
      const r = await api.post("/admin/aliexpress/import", { store_id: storeId, products });
      toast.success(`Imported ${r.data.imported} product${r.data.imported === 1 ? "" : "s"}`);
      setAliOpen(false); load(storeId);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setAliImporting(false); }
  };

  const openAmazon = async () => {
    setAmzOpen(true); setAmzResults([]); setAmzSelected([]); setAmzQ("");
    try { const r = await api.get("/admin/amazon/status"); setAmzConfigured(!!r.data.configured); }
    catch { setAmzConfigured(false); }
  };
  const amazonSearch = async () => {
    if (!amzQ.trim()) return;
    setAmzLoading(true);
    try { const r = await api.get(`/admin/amazon/search?q=${encodeURIComponent(amzQ.trim())}`); setAmzResults(r.data.products || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Search failed"); }
    finally { setAmzLoading(false); }
  };
  const toggleAmz = (p) => setAmzSelected((s) => s.some((x) => x.external_id === p.external_id) ? s.filter((x) => x.external_id !== p.external_id) : [...s, p]);
  const amazonImport = async () => {
    if (!storeId) { toast.error("Select a store first"); return; }
    setAmzImporting(true);
    try {
      const products = amzSelected.map((p) => ({ title: p.title, price: p.price, image_url: p.image_url, affiliate_url: p.affiliate_url }));
      const r = await api.post("/admin/amazon/import", { store_id: storeId, products });
      toast.success(`Imported ${r.data.imported} product${r.data.imported === 1 ? "" : "s"}`);
      setAmzOpen(false); load(storeId);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setAmzImporting(false); }
  };

  const optimizeImages = async () => {
    setOptimizing(true);
    let totalOpt = 0, totalSaved = 0;
    try {
      for (let i = 0; i < 300; i++) {
        const r = await api.post("/admin/optimize-images?limit=20");
        totalOpt += r.data.optimized; totalSaved += r.data.saved_bytes;
        if (r.data.done) break;
      }
      const mb = totalSaved / 1024 / 1024;
      toast.success(totalOpt
        ? `Optimized ${totalOpt} image${totalOpt === 1 ? "" : "s"} · saved ${mb >= 1 ? mb.toFixed(1) + " MB" : Math.round(totalSaved / 1024) + " KB"}`
        : "All images already optimized");
      load(storeId);
    } catch (e) { toast.error(e.response?.data?.detail || "Optimization failed"); }
    finally { setOptimizing(false); }
  };
  useEffect(() => { load(storeId); }, [storeId]);

  // deep-link from Leads page: /admin/products?scrape=<url>
  useEffect(() => {
    const u = searchParams.get("scrape");
    if (u) {
      setImportOpen(true);
      setImpMode("scrape");
      setScrapeUrl(u);
      searchParams.delete("scrape");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p }); setEditing(p.id); setOpen(true); };

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: res.data.url }));
      if (res.data.optimized) {
        const kb = (n) => `${Math.round(n / 1024)} KB`;
        toast.success(`Image optimized → WebP (${kb(res.data.original_size)} → ${kb(res.data.size)})`);
      }
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title || !form.price) { toast.error("Title and price are required"); return; }
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price), price_tiers: normalizeTiers(form.price_tiers) };
    try {
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post(`/stores/${storeId}/products`, payload);
      toast.success("Product saved");
      setOpen(false);
      load(storeId);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load(storeId);
  };

  const generateDesc = async (target, setTarget) => {
    if (!target.title) { toast.error("Add a product title first"); return; }
    setAiBusy(true);
    try {
      const r = await api.post("/products/generate-description", { title: target.title, category: target.category });
      setTarget((f) => ({ ...f, description: r.data.description }));
      toast.success("Description generated");
    } catch (e) { toast.error(e.response?.data?.detail || "AI generation failed"); }
    finally { setAiBusy(false); }
  };

  const toggleImpStore = (id) => setImpStores((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const parseCsv = (text) => {
    const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    return lines.slice(1).map((line) => {
      const cells = line.split(",");
      const row = {};
      headers.forEach((h, i) => { row[h] = (cells[i] || "").trim(); });
      return {
        title: row.title || "", price: parseFloat(row.price) || 0,
        category: row.category || "General", description: row.description || "",
        image_url: row.image_url || row.image || "", stock: parseInt(row.stock || "100", 10) || 0,
        in_stock: true, variant_name: "", variant_options: [],
      };
    }).filter((p) => p.title);
  };

  const submitImport = async () => {
    if (impStores.length === 0) { toast.error("Select at least one store to assign to"); return; }
    let products = [];
    if (impMode === "csv") {
      products = parseCsv(csvText);
      if (!products.length) { toast.error("No valid rows. Include a header row + at least one product."); return; }
    } else if (impMode === "scrape") {
      products = scraped.filter((p) => p._keep !== false).map(({ _keep, source_url, ...p }) => ({ ...p, price: parseFloat(p.price) || 0 }));
      if (!products.length) { toast.error("Nothing to import. Scrape a shop first."); return; }
    } else {
      if (!impForm.title || !impForm.price) { toast.error("Title and price are required"); return; }
      products = [{ ...impForm, price: parseFloat(impForm.price) }];
    }
    setImpBusy(true);
    try {
      const r = await api.post("/products/import", { store_ids: impStores, products });
      toast.success(`Imported ${r.data.created} product(s) across ${r.data.stores} store(s)`);
      setImportOpen(false); setCsvText(""); setImpForm(empty); setImpStores([]); setScraped([]); setScrapeUrl(""); setScrapePlatform("");
      load(storeId);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImpBusy(false); }
  };

  const enrichAll = async (rows) => {
    setEnriching(true); setEnrichProgress(0);
    const CHUNK = 20;
    const clean = rows.filter((p) => p._keep !== false);
    const enrichedOut = [...rows];
    try {
      for (let i = 0; i < clean.length; i += CHUNK) {
        const batch = clean.slice(i, i + CHUNK).map(({ _keep, source_url, ...p }) => ({ ...p, price: parseFloat(p.price) || 0 }));
        const r = await api.post("/products/enrich", { products: batch });
        r.data.products.forEach((ep, j) => {
          const orig = clean[i + j];
          const idx = enrichedOut.findIndex((x) => x === orig);
          if (idx >= 0) enrichedOut[idx] = { ...enrichedOut[idx], description: ep.description, category: ep.category };
        });
        setEnrichProgress(Math.min(100, Math.round(((i + batch.length) / clean.length) * 100)));
        setScraped([...enrichedOut]);
      }
      toast.success("AI enriched all products");
    } catch (e) { toast.error(e.response?.data?.detail || "AI enrichment failed"); }
    finally { setEnriching(false); }
    return enrichedOut;
  };

  const runScrape = async () => {
    const url = scrapeUrl.trim();
    if (!url) { toast.error("Enter a shop URL to scrape"); return; }
    setScrapeBusy(true); setScraped([]); setScrapePlatform("");
    try {
      const r = await api.post("/products/scrape", { url, limit: 100 });
      const rows = r.data.products.map((p) => ({ ...p, _keep: true }));
      setScraped(rows);
      setScrapePlatform(r.data.platform || "generic");
      toast.success(`Found ${rows.length} product(s)`);
      if (autoEnrich && rows.length) await enrichAll(rows);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not scrape that site"); }
    finally { setScrapeBusy(false); }
  };

  const updateScraped = (idx, patch) => setScraped((s) => s.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const keepCount = scraped.filter((p) => p._keep !== false).length;

  return (
    <div className="animate-fade-up" data-testid="products-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <span className="text-xs font-heading font-bold uppercase tracking-[0.2em] text-zinc-500">Manage</span>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-1">Products</h1>
        </div>
        <div className="flex items-center gap-3">
          <Select value={storeId} onValueChange={setSelectedStore}>
            <SelectTrigger className="w-52 rounded-full" data-testid="product-store-select"><SelectValue placeholder="Select store" /></SelectTrigger>
            <SelectContent>
              {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={optimizeImages} disabled={optimizing} data-testid="optimize-images-button" className="flex items-center gap-2 border border-zinc-200 rounded-full px-5 py-3 text-sm font-body font-semibold hover:bg-zinc-50 active:scale-95 transition-transform disabled:opacity-60">
            {optimizing ? <Loader2 size={16} className="animate-spin" /> : <ImageDown size={16} />} {optimizing ? "Optimizing…" : "Optimize images"}
          </button>
          <button onClick={openAli} disabled={!storeId} data-testid="aliexpress-import-button" className="flex items-center gap-2 border border-zinc-200 rounded-full px-5 py-3 text-sm font-body font-semibold hover:bg-zinc-50 active:scale-95 transition-transform disabled:opacity-50">
            <Globe size={16} /> AliExpress
          </button>
          <button onClick={openAmazon} disabled={!storeId} data-testid="amazon-import-button" className="flex items-center gap-2 border border-zinc-200 rounded-full px-5 py-3 text-sm font-body font-semibold hover:bg-zinc-50 active:scale-95 transition-transform disabled:opacity-50">
            <Globe size={16} /> Amazon
          </button>
          <button onClick={() => setImportOpen(true)} data-testid="import-products-button" className="flex items-center gap-2 border border-zinc-200 rounded-full px-5 py-3 text-sm font-body font-semibold hover:bg-zinc-50 active:scale-95 transition-transform">
            <FileUp size={16} /> Import
          </button>
          <button onClick={openNew} disabled={!storeId} data-testid="new-product-button" className="flex items-center gap-2 bg-black text-white rounded-full px-5 py-3 text-sm font-body font-semibold active:scale-95 transition-transform disabled:opacity-50">
            <Plus size={16} /> New product
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {products.map((p) => (
          <div key={p.id} className="bg-white border border-zinc-200/80 rounded-xl overflow-hidden group" data-testid={`product-row-${p.id}`}>
            <div className="aspect-[4/5] bg-zinc-100 overflow-hidden">
              <img src={imgUrl(p.image_url)} alt={p.title} className="h-full w-full object-cover" />
            </div>
            <div className="p-3">
              <div className="font-body font-semibold text-sm truncate">{p.title}</div>
              <div className="flex items-center justify-between mt-1">
                <span className="font-heading font-bold text-sm">{money(p.price)}</span>
                <span className="text-[10px] uppercase tracking-wider text-zinc-400">{p.category}</span>
              </div>
              <div className={`text-[11px] mt-1 font-semibold ${p.stock <= 0 ? "text-rose-500" : p.stock <= 5 ? "text-amber-600" : "text-zinc-400"}`}>
                {p.stock <= 0 ? "Sold out" : `${p.stock} in stock`}{p.variant_name ? ` · ${p.variant_name}` : ""}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="flex-1 flex items-center justify-center gap-1 border border-zinc-200 rounded-lg py-1.5 text-xs font-semibold hover:bg-zinc-50"><Pencil size={12} /> Edit</button>
                <button onClick={() => remove(p.id)} data-testid={`delete-product-${p.id}`} className="border border-zinc-200 rounded-lg py-1.5 px-2.5 text-xs hover:bg-rose-50 hover:text-rose-500"><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {products.length === 0 && <p className="text-center text-zinc-400 py-20 font-body">No products yet. Add your first one.</p>}

      {/* AliExpress affiliate import */}
      <Dialog open={aliOpen} onOpenChange={setAliOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">Import from AliExpress</DialogTitle></DialogHeader>
          {!aliConfigured ? (
            <div className="text-sm text-zinc-600 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="ali-not-configured">
              AliExpress isn't connected yet. Add your <b>App Key</b>, <b>App Secret</b> and <b>Tracking ID</b> to the server to activate affiliate import.
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <input data-testid="ali-search-input" value={aliQ} onChange={(e) => setAliQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && aliSearch()} placeholder="Search AliExpress products…" className="pinput flex-1" />
                <button onClick={aliSearch} disabled={aliLoading} data-testid="ali-search-button" className="bg-black text-white rounded-xl px-5 text-sm font-semibold disabled:opacity-50 flex items-center">{aliLoading ? <Loader2 size={16} className="animate-spin" /> : "Search"}</button>
              </div>
              <div className="max-h-[52vh] overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-3 mt-1">
                {aliResults.map((p) => {
                  const sel = aliSelected.some((x) => x.external_id === p.external_id);
                  return (
                    <button key={p.external_id} onClick={() => toggleAli(p)} data-testid={`ali-result-${p.external_id}`}
                      className={`text-left border rounded-xl overflow-hidden transition-all ${sel ? "border-black ring-2 ring-black" : "border-zinc-200 hover:border-zinc-400"}`}>
                      <div className="aspect-square bg-zinc-100"><img src={p.image_url} alt="" className="w-full h-full object-cover" /></div>
                      <div className="p-2">
                        <div className="text-xs line-clamp-2 h-8 text-zinc-700">{p.title}</div>
                        <div className="text-sm font-bold mt-1">{money(p.price)}</div>
                      </div>
                    </button>
                  );
                })}
                {!aliLoading && aliResults.length === 0 ? <div className="col-span-full text-center text-sm text-zinc-400 py-10">Search AliExpress to find products to import.</div> : null}
              </div>
            </>
          )}
          <DialogFooter>
            <button onClick={() => setAliOpen(false)} className="text-sm text-zinc-500 px-4">Cancel</button>
            <button onClick={aliImport} disabled={!aliConfigured || aliSelected.length === 0 || aliImporting} data-testid="ali-import-button" className="bg-black text-white rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50">
              {aliImporting ? "Importing…" : `Import ${aliSelected.length || ""} to this store`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Amazon affiliate import */}
      <Dialog open={amzOpen} onOpenChange={setAmzOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">Import from Amazon</DialogTitle></DialogHeader>
          {!amzConfigured ? (
            <div className="text-sm text-zinc-600 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="amz-not-configured">
              Amazon isn't connected yet. Add your <b>Access Key</b>, <b>Secret Key</b> and <b>Partner Tag</b> to the server to activate affiliate import.
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <input data-testid="amz-search-input" value={amzQ} onChange={(e) => setAmzQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && amazonSearch()} placeholder="Search Amazon products…" className="pinput flex-1" />
                <button onClick={amazonSearch} disabled={amzLoading} data-testid="amz-search-button" className="bg-black text-white rounded-xl px-5 text-sm font-semibold disabled:opacity-50 flex items-center">{amzLoading ? <Loader2 size={16} className="animate-spin" /> : "Search"}</button>
              </div>
              <div className="max-h-[52vh] overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-3 mt-1">
                {amzResults.map((p) => {
                  const sel = amzSelected.some((x) => x.external_id === p.external_id);
                  return (
                    <button key={p.external_id} onClick={() => toggleAmz(p)} data-testid={`amz-result-${p.external_id}`}
                      className={`text-left border rounded-xl overflow-hidden transition-all ${sel ? "border-black ring-2 ring-black" : "border-zinc-200 hover:border-zinc-400"}`}>
                      <div className="aspect-square bg-zinc-100"><img src={p.image_url} alt="" className="w-full h-full object-cover" /></div>
                      <div className="p-2">
                        <div className="text-xs line-clamp-2 h-8 text-zinc-700">{p.title}</div>
                        <div className="text-sm font-bold mt-1">{money(p.price)}</div>
                      </div>
                    </button>
                  );
                })}
                {!amzLoading && amzResults.length === 0 ? <div className="col-span-full text-center text-sm text-zinc-400 py-10">Search Amazon to find products to import.</div> : null}
              </div>
            </>
          )}
          <DialogFooter>
            <button onClick={() => setAmzOpen(false)} className="text-sm text-zinc-500 px-4">Cancel</button>
            <button onClick={amazonImport} disabled={!amzConfigured || amzSelected.length === 0 || amzImporting} data-testid="amz-import-button" className="bg-black text-white rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50">
              {amzImporting ? "Importing…" : `Import ${amzSelected.length || ""} to this store`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="h-20 w-16 rounded-lg overflow-hidden bg-zinc-100">
                <img src={form.image_url ? imgUrl(form.image_url) : "https://images.unsplash.com/photo-1512566793746-0f89bfe3cd06?w=200&q=80"} alt="" className="h-full w-full object-cover" />
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="product-image-input" />
                <button onClick={() => fileRef.current.click()} className="flex items-center gap-2 border border-zinc-200 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-zinc-50" data-testid="product-image-upload">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload image
                </button>
              </div>
            </div>
            <Field label="Title"><input data-testid="product-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="pinput" /></Field>
            <Field label="Badge label (optional)">
              <input data-testid="product-label-input" value={form.label || ""} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. New · Bestseller · Bulk deal" maxLength={24} className="pinput" />
              <div className="text-[11px] text-zinc-400 mt-1">Shows as a small badge on the product. Hidden from Google search results.</div>
            </Field>
            <Field label="Custom text (optional)">
              <input data-testid="product-custom-text-input" value={form.custom_text || ""} onChange={(e) => setForm({ ...form, custom_text: e.target.value })} placeholder="Shown in the product popup" maxLength={120} className="pinput" />
              <div className="text-[11px] text-zinc-400 mt-1">Appears under the description in the product popup. Hidden from Google search results.</div>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Price (€)"><input data-testid="product-price-input" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="pinput" /></Field>
              <Field label="Category"><input data-testid="product-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="pinput" /></Field>
            </div>
            <Field label="Description">
              <div className="relative">
                <textarea data-testid="product-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="pinput" />
                <button type="button" onClick={() => generateDesc(form, setForm)} disabled={aiBusy} data-testid="ai-generate-desc"
                  className="absolute right-2 top-2 inline-flex items-center gap-1 text-xs font-semibold bg-black text-white rounded-full px-2.5 py-1 disabled:opacity-60 active:scale-95 transition-transform">
                  {aiBusy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} AI write
                </button>
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Stock quantity"><input data-testid="product-stock-input" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value || "0", 10) })} className="pinput" /></Field>
              <Field label="Variant name (optional)"><input data-testid="product-variant-name-input" value={form.variant_name} onChange={(e) => setForm({ ...form, variant_name: e.target.value })} placeholder="e.g. Size" className="pinput" /></Field>
            </div>
            <Field label="Variant options (comma-separated)">
              <input
                data-testid="product-variant-options-input"
                value={(form.variant_options || []).join(", ")}
                onChange={(e) => setForm({ ...form, variant_options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                placeholder="e.g. S, M, L, XL"
                className="pinput"
              />
            </Field>
            <div className="border-t border-zinc-100 pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-zinc-700">Wholesale bundles <span className="font-normal text-zinc-400">(optional — customers tap to buy the exact quantity)</span></label>
                {(form.price_tiers || []).length === 0 ? (
                  <button type="button" data-testid="prefill-tiers-button" onClick={() => setForm({ ...form, price_tiers: DEFAULT_TIERS.map((t) => ({ ...t })) })}
                    className="text-xs font-semibold text-black underline underline-offset-2 hover:text-zinc-600">Prefill standard tiers</button>
                ) : null}
              </div>
              {(form.price_tiers || []).length > 0 && (
                <div className="space-y-2" data-testid="tier-editor">
                  {form.price_tiers.map((t, ti) => (
                    <div key={ti} className="flex items-center gap-2" data-testid={`tier-row-${ti}`}>
                      <div className="flex items-center gap-1.5 flex-1">
                        <input type="number" min="1" value={t.min_qty} data-testid={`tier-qty-${ti}`}
                          onChange={(e) => setForm({ ...form, price_tiers: form.price_tiers.map((x, xi) => xi === ti ? { ...x, min_qty: e.target.value } : x) })}
                          className="pinput w-24" placeholder="Qty" />
                        <span className="text-xs text-zinc-400">pcs for</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-1">
                        <span className="text-xs text-zinc-400">€</span>
                        <input type="number" step="0.01" min="0" value={t.price} data-testid={`tier-price-${ti}`}
                          onChange={(e) => setForm({ ...form, price_tiers: form.price_tiers.map((x, xi) => xi === ti ? { ...x, price: e.target.value } : x) })}
                          className="pinput flex-1" placeholder="Total price" />
                      </div>
                      <button type="button" data-testid={`tier-remove-${ti}`} onClick={() => setForm({ ...form, price_tiers: form.price_tiers.filter((_, xi) => xi !== ti) })}
                        className="text-zinc-300 hover:text-rose-500 shrink-0 p-1"><Trash2 size={15} /></button>
                    </div>
                  ))}
                  <button type="button" data-testid="tier-add-button" onClick={() => setForm({ ...form, price_tiers: [...form.price_tiers, { min_qty: "", price: "" }] })}
                    className="inline-flex items-center gap-1 text-xs font-semibold border border-zinc-200 rounded-full px-3 py-1.5 hover:bg-zinc-50"><Plus size={13} /> Add tier</button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} disabled={saving} data-testid="save-product-button" className="bg-black text-white rounded-full px-6 py-3 text-sm font-semibold active:scale-95 transition-transform disabled:opacity-60 flex items-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />} Save product
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Import products</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Assign to stores</label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-auto p-1" data-testid="import-store-list">
                {stores.map((s) => (
                  <button key={s.id} type="button" onClick={() => toggleImpStore(s.id)} data-testid={`import-store-${s.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${impStores.includes(s.id) ? "bg-black text-white border-black" : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>
                    {s.name}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-zinc-400 mt-1">Each product is added as an independent copy to every selected store.</p>
            </div>

            <div className="flex gap-2">
              {["scrape", "csv", "manual"].map((m) => (
                <button key={m} type="button" onClick={() => setImpMode(m)} data-testid={`import-mode-${m}`}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold border flex items-center justify-center gap-1.5 ${impMode === m ? "bg-black text-white border-black" : "bg-white border-zinc-200 text-zinc-600"}`}>
                  {m === "scrape" ? <><Globe size={14} /> Scrape URL</> : m === "csv" ? "CSV bulk" : "Single product"}
                </button>
              ))}
            </div>

            {impMode === "scrape" ? (
              <div className="space-y-3" data-testid="scrape-panel">
                <div className="flex gap-2">
                  <input data-testid="scrape-url-input" value={scrapeUrl} onChange={(e) => setScrapeUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") runScrape(); }}
                    placeholder="https://client-webshop.com" className="pinput flex-1" />
                  <button type="button" onClick={runScrape} disabled={scrapeBusy || enriching} data-testid="scrape-run-button"
                    className="bg-black text-white rounded-full px-5 text-sm font-semibold active:scale-95 transition-transform disabled:opacity-60 flex items-center gap-2 whitespace-nowrap">
                    {scrapeBusy ? <Loader2 size={14} className="animate-spin" /> : <Globe size={14} />} Scrape
                  </button>
                </div>
                <label className="flex items-center gap-2 text-xs text-zinc-500 font-body cursor-pointer">
                  <input type="checkbox" checked={autoEnrich} onChange={(e) => setAutoEnrich(e.target.checked)} data-testid="scrape-autoenrich" />
                  Auto-improve descriptions & categories with AI after scraping
                </label>
                <p className="text-[11px] text-zinc-400">Detects Shopify, structured (JSON-LD) shops and generic stores. Imports up to 100 products. Note: some large stores block automated access.</p>

                {enriching && (
                  <div className="text-xs text-zinc-500 flex items-center gap-2" data-testid="enrich-progress">
                    <Loader2 size={13} className="animate-spin" /> AI enriching… {enrichProgress}%
                  </div>
                )}

                {scraped.length > 0 && (
                  <div className="space-y-2" data-testid="scrape-results">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-zinc-600">{keepCount} selected · {scrapePlatform} shop</span>
                      <button type="button" onClick={() => enrichAll(scraped)} disabled={enriching} data-testid="scrape-enrich-all"
                        className="inline-flex items-center gap-1 text-xs font-semibold border border-zinc-200 rounded-full px-3 py-1.5 hover:bg-zinc-50 disabled:opacity-60">
                        <Wand2 size={12} /> AI enrich all
                      </button>
                    </div>
                    <div className="border border-zinc-200 rounded-xl divide-y divide-zinc-100 max-h-[46vh] overflow-y-auto">
                      {scraped.map((p, i) => (
                        <div key={i} className={`flex gap-3 p-2.5 ${p._keep === false ? "opacity-40" : ""}`} data-testid={`scrape-row-${i}`}>
                          <div className="h-14 w-12 rounded-lg overflow-hidden bg-zinc-100 shrink-0">
                            {p.image_url ? <img src={p.image_url} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} /> : null}
                          </div>
                          <div className="flex-1 min-w-0 space-y-1">
                            <input value={p.title} onChange={(e) => updateScraped(i, { title: e.target.value })} data-testid={`scrape-title-${i}`}
                              className="w-full text-sm font-semibold border-0 border-b border-transparent hover:border-zinc-200 focus:border-black outline-none bg-transparent" />
                            <div className="flex gap-2">
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-zinc-400">€</span>
                                <input type="number" value={p.price} onChange={(e) => updateScraped(i, { price: e.target.value })} data-testid={`scrape-price-${i}`}
                                  className="w-20 text-xs border border-zinc-200 rounded px-1.5 py-0.5 outline-none focus:border-black" />
                              </div>
                              <input value={p.category} onChange={(e) => updateScraped(i, { category: e.target.value })} data-testid={`scrape-category-${i}`}
                                placeholder="Category" className="flex-1 text-xs border border-zinc-200 rounded px-1.5 py-0.5 outline-none focus:border-black" />
                            </div>
                            {p.description ? <p className="text-[11px] text-zinc-400 line-clamp-1">{p.description}</p> : null}
                          </div>
                          <button type="button" onClick={() => updateScraped(i, { _keep: p._keep === false })} data-testid={`scrape-toggle-${i}`}
                            className="self-start text-zinc-300 hover:text-rose-500 shrink-0" title={p._keep === false ? "Include" : "Exclude"}>
                            {p._keep === false ? <Plus size={16} /> : <X size={16} />}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : impMode === "csv" ? (
              <div className="space-y-2">
                <div className="text-[11px] text-zinc-500 bg-zinc-50 rounded-lg p-2 font-mono">Header row: <b>title,price,category,description,image_url,stock</b></div>
                <textarea data-testid="import-csv-text" value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={6}
                  placeholder={"title,price,category,description,image_url,stock\nLeather Wallet,72.99,Accessories,Handmade full-grain wallet,https://...,25"}
                  className="pinput font-mono text-xs" />
                <div>
                  <input ref={csvFileRef} type="file" accept=".csv,text/csv" hidden data-testid="import-csv-file"
                    onChange={(e) => { const f = e.target.files[0]; if (f) { const rd = new FileReader(); rd.onload = () => setCsvText(String(rd.result)); rd.readAsText(f); } }} />
                  <button type="button" onClick={() => csvFileRef.current.click()} className="flex items-center gap-2 border border-zinc-200 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-zinc-50">
                    <Upload size={14} /> Upload .csv file
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <Field label="Title"><input data-testid="import-title" value={impForm.title} onChange={(e) => setImpForm({ ...impForm, title: e.target.value })} className="pinput" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Price (€)"><input data-testid="import-price" type="number" value={impForm.price} onChange={(e) => setImpForm({ ...impForm, price: e.target.value })} className="pinput" /></Field>
                  <Field label="Category"><input data-testid="import-category" value={impForm.category} onChange={(e) => setImpForm({ ...impForm, category: e.target.value })} className="pinput" /></Field>
                </div>
                <Field label="Image URL"><input data-testid="import-image" value={impForm.image_url} onChange={(e) => setImpForm({ ...impForm, image_url: e.target.value })} placeholder="https://" className="pinput" /></Field>
                <Field label="Description">
                  <div className="relative">
                    <textarea data-testid="import-desc" value={impForm.description} onChange={(e) => setImpForm({ ...impForm, description: e.target.value })} rows={3} className="pinput" />
                    <button type="button" onClick={() => generateDesc(impForm, setImpForm)} disabled={aiBusy} data-testid="import-ai-generate"
                      className="absolute right-2 top-2 inline-flex items-center gap-1 text-xs font-semibold bg-black text-white rounded-full px-2.5 py-1 disabled:opacity-60">
                      {aiBusy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} AI write
                    </button>
                  </div>
                </Field>
                <Field label="Stock quantity"><input data-testid="import-stock" type="number" value={impForm.stock} onChange={(e) => setImpForm({ ...impForm, stock: parseInt(e.target.value || "0", 10) })} className="pinput" /></Field>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={submitImport} disabled={impBusy || enriching} data-testid="import-submit-button" className="bg-black text-white rounded-full px-6 py-3 text-sm font-semibold active:scale-95 transition-transform disabled:opacity-60 flex items-center gap-2">
              {impBusy && <Loader2 size={14} className="animate-spin" />}
              {impMode === "scrape" ? `Import ${keepCount} to ${impStores.length} store${impStores.length === 1 ? "" : "s"}` : `Import to ${impStores.length} store${impStores.length === 1 ? "" : "s"}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`.pinput{width:100%;border:1px solid #e4e4e7;border-radius:12px;padding:10px 14px;font-size:14px;outline:none;font-family:'Plus Jakarta Sans'}.pinput:focus{border-color:#4F46E5}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">{label}</label>
    {children}
  </div>
);
